import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MetricCardsComponent, type MetricCard } from './components/metric-cards/metric-cards.component';
import { StatisticsChartComponent, type ChartData } from './components/statistics-chart/statistics-chart.component';
import { EstimatedRevenueComponent, type RevenueData } from './components/estimated-revenue/estimated-revenue.component';
import { SalesCategoryComponent, type CategoryData } from './components/sales-category/sales-category.component';
import { UpcomingScheduleComponent, type ScheduleItem } from './components/upcoming-schedule/upcoming-schedule.component';
import { RecentOrdersComponent, type OrderItem } from './components/recent-orders/recent-orders.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MetricCardsComponent,
    StatisticsChartComponent,
    EstimatedRevenueComponent,
    SalesCategoryComponent,
    UpcomingScheduleComponent,
    RecentOrdersComponent
  ],
  template: `
    <div class="space-y-6">
      <!-- Header Section -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900 font-outfit">Dashboard</h1>
          <p class="text-gray-600 mt-1">Welcome back! Here's what's happening with your business today.</p>
        </div>
        <div class="text-sm text-gray-500">
          {{ currentDate | date:'fullDate' }}
        </div>
      </div>

      <!-- Metrics Cards -->
      <div>
        <app-metric-cards [metrics]="metricsData"></app-metric-cards>
      </div>

      <!-- Charts Section -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Statistics Chart -->
        <div class="lg:col-span-2">
          <app-statistics-chart 
            title="Statistics" 
            [chartData]="chartData">
          </app-statistics-chart>
        </div>

        <!-- Estimated Revenue -->
        <app-estimated-revenue 
          title="Estimated Revenue"
          [revenueData]="revenueData">
        </app-estimated-revenue>
      </div>

      <!-- Bottom Section -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Sales by Category -->
        <app-sales-category 
          title="Sales by Category"
          [categories]="categoriesData">
        </app-sales-category>

        <!-- Upcoming Schedule -->
        <app-upcoming-schedule 
          title="Upcoming Schedule"
          [scheduleItems]="scheduleData">
        </app-upcoming-schedule>

        <!-- Recent Orders -->
        <app-recent-orders 
          title="Recent Orders"
          [recentOrders]="ordersData">
        </app-recent-orders>
      </div>
    </div>
  `,
  styles: ``
})
export class DashboardComponent {
  currentDate = new Date();

  metricsData: MetricCard[] = [
    {
      title: 'Total Revenue',
      value: '$67,523.45',
      change: '+42.7% from last month',
      changeType: 'positive',
      icon: 'revenue',
      iconColor: 'brand'
    },
    {
      title: 'Subscriptions',
      value: '+3,847',
      change: '+63.8% from last month',
      changeType: 'positive',
      icon: 'subscriptions',
      iconColor: 'orange'
    },
    {
      title: 'Sales',
      value: '+15,926',
      change: '+29.8% from last month',
      changeType: 'positive',
      icon: 'sales',
      iconColor: 'blue'
    },
    {
      title: 'Active Now',
      value: '+1,247',
      change: '+117% since last hour',
      changeType: 'positive',
      icon: 'active',
      iconColor: 'green'
    }
  ];

  chartData: ChartData[] = [
    { period: 'January', value: 25000 },
    { period: 'February', value: 28500 },
    { period: 'March', value: 32200 },
    { period: 'April', value: 35800 },
    { period: 'May', value: 39200 },
    { period: 'June', value: 42600 },
    { period: 'July', value: 46800 },
    { period: 'August', value: 50200 },
    { period: 'September', value: 54500 },
    { period: 'October', value: 58200 },
    { period: 'November', value: 62800 },
    { period: 'December', value: 67500 }
  ];

  revenueData: RevenueData = {
    current: 67500,
    target: 75000,
    percentage: 90
  };

  categoriesData: CategoryData[] = [
    { name: 'Electronics', value: 18500, color: '#b55299' },
    { name: 'Clothing', value: 14200, color: '#f59e0b' },
    { name: 'Books', value: 8600, color: '#0ba5ec' },
    { name: 'Home & Garden', value: 6300, color: '#10b981' },
    { name: 'Sports', value: 4800, color: '#8b5cf6' }
  ];

  scheduleData: ScheduleItem[] = [
    {
      id: '1',
      title: 'Team Meeting',
      time: 'Today at 2:00 PM',
      type: 'meeting',
      priority: 'high'
    },
    {
      id: '2',
      title: 'Product Launch',
      time: '10:00 AM',
      date: 'Tomorrow',
      type: 'event',
      priority: 'high'
    },
    {
      id: '3',
      title: 'Client Call',
      time: '3:30 PM',
      date: 'Friday',
      type: 'call',
      priority: 'medium'
    },
    {
      id: '4',
      title: 'Review Session',
      time: '9:00 AM',
      date: 'Monday',
      type: 'review',
      priority: 'medium'
    },
    {
      id: '5',
      title: 'Update Documentation',
      time: '11:00 AM',
      date: 'Wednesday',
      type: 'task',
      priority: 'low',
      completed: false
    }
  ];

  ordersData: OrderItem[] = [
    {
      id: '1',
      customerName: 'John Doe',
      customerInitials: 'JD',
      orderNumber: '#12845',
      amount: 1299,
      status: 'completed',
      date: new Date()
    },
    {
      id: '2',
      customerName: 'Jane Smith',
      customerInitials: 'JS',
      orderNumber: '#12846',
      amount: 849,
      status: 'processing',
      date: new Date(Date.now() - 86400000) // Yesterday
    },
    {
      id: '3',
      customerName: 'Mike Brown',
      customerInitials: 'MB',
      orderNumber: '#12847',
      amount: 2199,
      status: 'completed',
      date: new Date(Date.now() - 172800000) // 2 days ago
    },
    {
      id: '4',
      customerName: 'Sarah Wilson',
      customerInitials: 'SW',
      orderNumber: '#12848',
      amount: 599,
      status: 'pending',
      date: new Date(Date.now() - 259200000) // 3 days ago
    },
    {
      id: '5',
      customerName: 'David Lee',
      customerInitials: 'DL',
      orderNumber: '#12849',
      amount: 399,
      status: 'completed',
      date: new Date(Date.now() - 345600000) // 4 days ago
    },
    {
      id: '6',
      customerName: 'Emma Davis',
      customerInitials: 'ED',
      orderNumber: '#12850',
      amount: 1599,
      status: 'processing',
      date: new Date(Date.now() - 432000000) // 5 days ago
    }
  ];
}